import {
  DELAI_DE_MODIFICATION_MS,
  UNREAD_CAP,
  type CallNote,
  type Conversation,
  type Message,
  type PublicUser,
  type ReportReason,
  VoiceMessage,
} from '@sirocco/shared';
import {
  onChildAdded,
  onChildChanged,
  onDisconnect,
  onValue,
  query,
  ref,
  orderByChild,
  remove,
  set,
  startAfter,
} from 'firebase/database';
import { db } from './app';
import { requireUid } from './auth';
import { listBlocked } from './blocks';
import { listFriends } from './friends';
import { uploadIfDataUrl, uploadMedia } from './media';
import { paths } from './paths';
import type { VideoAEnvoyer } from '../utils/video';
import { keysOf, loadUsers, toIso, unknownUser } from './records';
import {
  newKey,
  pushValue,
  readKeys,
  readList,
  readMany,
  readOptionalValue,
  readValue,
  removeValue,
  SiroccoDataError,
  updateValues,
  writeValue,
} from './rtdb';
import { t } from '../i18n';

/**
 * Messagerie.
 *
 * La base ne garde que le strict necessaire : les membres en `{ uid: true }`,
 * les dates en millisecondes, et le fil a plat sous `messages/{id}`. Tout ce
 * que l'ecran affiche - profils, dernier message, non-lus, titre - se
 * recompose ici a la lecture.
 */

/**
 * Combien de messages suffisent pour resumer un fil.
 *
 * La liste des conversations et la pastille de l'onglet n'ont besoin que
 * du dernier message et d'un nombre. Elles lisaient pourtant chaque fil
 * en entier : 1,7 Mo pour afficher dix apercus, retelecharges a chaque
 * affichage. Une fenetre sur la fin du fil donne exactement la meme
 * reponse, sauf si plus de trente messages attendent — auquel cas le
 * compte s'arrete la et la pastille affiche « 30+ ». Personne ne lit un
 * nombre au-dela.
 *
 * Le fil ouvert, lui, n'est pas rogne : voir `listThread`.
 */
const APERCU = UNREAD_CAP;

/** Une conversation, telle qu'elle est ecrite dans `conversations/{id}`. */
interface ConversationRecord {
  serverId: string;
  type: 'direct' | 'group';
  title: string;
  description: string;
  adminId: string | null;
  createdAt: number;
  /** Photo du groupe, en data URL. Voir `utils/image`. */
  avatarUrl?: string | null;
  /**
   * L'evenement dont ce groupe est la conversation.
   *
   * Ce qui distingue « le groupe de la sortie » d'un groupe ordinaire.
   * Le lien existait deja dans l'autre sens — `events/{spot}/{id}/groupId`
   * — mais depuis la conversation on ne savait pas d'ou l'on venait, et
   * les regles n'avaient rien pour refuser une invitation.
   *
   * On n'y entre pas sur invitation : on rejoint la sortie.
   */
  eventId?: string | null;
  /**
   * L'association dont ce groupe est la conversation.
   *
   * Tenu par la fonction `tenirLeGroupeDeLAsso` : on y entre en rejoignant
   * l'association, jamais sur invitation, et son chef l'administre.
   */
  asso?: string | null;
  members?: Record<string, true>;
  /** Membres ayant coupe les notifications : le silence est individuel. */
  mutedBy?: Record<string, true>;
  /**
   * Ancienne echeance de la porte « organisateur d'evenement ».
   *
   * Plus rien ne l'ecrit ni ne la lit : la demande de message a remplace
   * cette exception, qui n'ouvrait de toute facon que le champ de saisie
   * — l'envoi, lui, ne regardait que l'amitie. Le champ reste declare
   * parce que des conversations le portent encore.
   */
  openUntil?: number;
  /**
   * Demande de message en attente, et qui l'a envoyee.
   *
   * Presente, la conversation n'existe qu'a moitie : son auteur n'a droit
   * qu'a un message, et elle ne figure pas dans la liste principale mais
   * dans les demandes. Le destinataire la fait disparaitre en repondant —
   * c'est sa reponse, et rien d'autre, qui ouvre la conversation.
   */
  request?: string;
  /** Depuis quand elle attend. Sert a savoir si son message est parti. */
  requestAt?: number;
  /**
   * Demande acceptee : la conversation reste ouverte a l'ecriture.
   *
   * Retirer `request` ne suffisait pas. Les deux ne sont pas amis pour
   * autant, et la conversation revenait dans la liste principale... fermee
   * a l'ecriture. Il fallait donc garder la trace du geste : quelqu'un a
   * repondu, il a choisi de parler a cette personne.
   */
  opened?: boolean;
  /*
    Le message unique d'une demande est parti.

    Les regles ne savent pas compter les messages d'un fil : elles se
    fiaient a « le fil est vide », ce qui n'est vrai que d'une conversation
    toute neuve. Entre deux anciens amis, l'histoire est la — et la ligne
    « vous n'etes plus amis » suffisait a la remplir : la demande etait
    refusee avant d'avoir servi. Ce drapeau dit la seule chose qui compte.
  */
  requestSent?: boolean;
}

/** Un message, tel qu'il est ecrit dans `messages/{conversationId}/{id}`. */
interface MessageRecord {
  senderId: string;
  content: string;
  createdAt: number;
  attachedEventId: string | null;
  /** Post partage : l'identifiant, jamais une copie du texte. */
  attachedPostId?: string | null;
  /** Le message auquel celui-ci repond : l'identifiant, jamais une copie. */
  replyTo?: string | null;
  imageUrl?: string | null;
  /**
   * Le rapport de la photo, retenu a l'envoi.
   *
   * Sans lui, la bulle ne connait sa taille qu'une fois l'image arrivee,
   * et le fil s'allonge sous la position de lecture. Les photos d'avant
   * n'en ont pas : elles gardent le cadre par defaut.
   */
  imageRatio?: number;
  /*
    Le vocal, a plat plutot qu'en objet : la Realtime Database range mieux
    trois champs simples qu'un noeud, et une valeur absente y disparait
    d'elle-meme.
  */
  audioUrl?: string | null;
  audioDurationMs?: number;
  audioPeaks?: number[];
  /* La video, a plat comme le vocal et pour la meme raison. */
  videoUrl?: string | null;
  videoDurationMs?: number;
  /** Une image du film. Absente sur les videos d'avant. */
  videoPosterUrl?: string | null;
  /* La trace d'un appel, a plat comme le reste. */
  callMissed?: boolean;
  callVideo?: boolean;
  callDurationMs?: number;
  /** Retire par son auteur : l'enregistrement reste, vide. */
  deleted?: boolean;
  /** Corrige par son auteur : la date de la derniere correction. */
  editedAt?: number;
  /** Ce qui est arrive au fil, et que personne n'a ecrit. */
  systeme?: 'amitie-rompue' | 'sortie-groupe' | 'retire-groupe' | 'ajout-groupe';
}

/**
 * Chemin du silence d'un membre.
 * `paths` ne l'expose pas encore : on le derive de la conversation plutot
 * que de reecrire la racine a la main.
 */
function mutedByPath(conversationId: string, uid: string): string {
  return `${paths.conversation(conversationId)}/mutedBy/${uid}`;
}

/** `['a', 'b']` -> `{ a: true, b: true }`, la forme des index de la base. */
function membersMap(uids: string[]): Record<string, true> {
  const map: Record<string, true> = {};
  for (const uid of uids) map[uid] = true;
  return map;
}

/**
 * Horodatages de lecture de tous les membres d'une conversation.
 * `lastRead` n'est indexe que par membre : on lit chaque feuille, mais
 * les lectures partent ensemble.
 */
async function readLastRead(
  conversationId: string,
  members: string[],
): Promise<Map<string, number>> {
  const values = await Promise.all(
    members.map((member) => readValue<number>(paths.lastRead(conversationId, member))),
  );
  const seen = new Map<string, number>();
  members.forEach((member, index) => seen.set(member, values[index] ?? 0));
  return seen;
}

/**
 * Un message compose.
 * "Lu" veut dire lu par les destinataires : tous les membres autres que
 * l'expediteur ont ouvert le fil apres l'envoi.
 */
function toMessage(
  conversationId: string,
  record: MessageRecord & { id: string },
  seen: Map<string, number>,
  members: string[],
): Message {
  return {
    id: record.id,
    conversationId,
    senderId: record.senderId,
    content: record.content,
    createdAt: toIso(record.createdAt),
    read: members.every(
      (member) => member === record.senderId || (seen.get(member) ?? 0) >= record.createdAt,
    ),
    attachedEventId: record.attachedEventId ?? null,
    attachedPostId: record.attachedPostId ?? null,
    replyTo: record.replyTo ?? null,
    imageUrl: record.imageUrl ?? null,
    imageRatio: record.imageRatio ?? null,
    voice: record.audioUrl
      ? {
          audioUrl: record.audioUrl,
          durationMs: record.audioDurationMs ?? 0,
          peaks: record.audioPeaks ?? [],
        }
      : null,
    video: record.videoUrl
      ? {
          videoUrl: record.videoUrl,
          durationMs: record.videoDurationMs ?? 0,
          posterUrl: record.videoPosterUrl ?? null,
        }
      : null,
    call:
      record.callMissed === undefined
        ? null
        : {
            missed: record.callMissed,
            video: Boolean(record.callVideo),
            durationMs: record.callDurationMs ?? 0,
          },
    deleted: Boolean(record.deleted),
    systeme: record.systeme ?? null,
    modifie: typeof record.editedAt === 'number',
  };
}

/** Lit une conversation en verifiant que j'y ai bien ma place. */
async function requireMembership(conversationId: string, uid: string): Promise<ConversationRecord> {
  const record = await readValue<ConversationRecord>(paths.conversation(conversationId));
  if (!record) throw new SiroccoDataError(t.messages.erreurs.plusDeConversation, 'no-conversation');
  if (!record.members?.[uid]) {
    throw new SiroccoDataError(t.messages.erreurs.pasMembre, 'not-a-member');
  }
  return record;
}

/**
 * On n'ecrit en prive qu'a un ami.
 *
 * Le blocage passe par la meme porte sans avoir a lire la liste de
 * l'autre — que les regles nous interdisent, a juste titre : bloquer
 * rompt l'amitie des deux cotes, donc l'un comme l'autre se retrouve
 * hors de cette condition. Les groupes, eux, ne demandent rien : on y
 * invite qui l'on veut.
 */
/**
 * Ai-je deja ecrit depuis que la demande est ouverte ?
 *
 * La question se pose parce qu'une demande peut naitre sur un fil qui a
 * une histoire — deux anciens amis. Compter les messages ne dirait rien ;
 * ce qui compte, c'est ceux qui suivent l'ouverture.
 *
 * Sans date d'ouverture, le fil est neuf : sa seule existence repond.
 */
async function messageDepuis(
  conversationId: string,
  uid: string,
  depuis: number | undefined,
): Promise<boolean> {
  const thread = await readList<MessageRecord>(paths.messages(conversationId), {
    orderBy: 'createdAt',
    limit: APERCU,
  });
  if (depuis === undefined) return thread.length > 0;
  return thread.some((message) => message.senderId === uid && message.createdAt >= depuis);
}

/**
 * Un refus des regles, reconnu parmi les autres echecs.
 *
 * Dans un tete-a-tete ou tout le reste a ete verifie ici meme — membre,
 * amitie, demande —, il ne reste qu'une raison : l'autre m'a bloque. Sa
 * liste ne se lit pas, a juste titre ; on ne l'apprend qu'au refus, et un
 * `PERMISSION_DENIED` brut ne dirait rien a personne.
 */
function estUnRefus(cause: unknown): boolean {
  const { code, message } = (cause ?? {}) as { code?: string; message?: string };
  return /permission[_ ]denied/i.test(`${code ?? ''} ${message ?? ''}`);
}

function injoignable(): SiroccoDataError {
  return new SiroccoDataError(t.messages.erreurs.injoignable, 'unreachable');
}

async function requireFriendship(
  conversationId: string,
  record: ConversationRecord,
  uid: string,
): Promise<void> {
  if (record.type !== 'direct') return;

  const other = keysOf(record.members).find((member) => member !== uid);
  if (!other) return;

  /*
    Une demande en attente se passe d'amitie — c'est tout son objet. Mais
    celui qui l'a ouverte n'a droit qu'a un message : la conversation ne
    s'ouvre que si l'autre repond. Les regles disent la meme chose, et
    c'est la qu'elle est tenue ; ici on la dit en francais plutot que de
    laisser tomber un refus opaque.
  */
  if (record.request) {
    if (record.request !== uid) return;
    if (await messageDepuis(conversationId, uid, record.requestAt)) {
      throw new SiroccoDataError(
        t.messages.erreurs.demandeEnAttente,
        'request-pending',
      );
    }
    return;
  }

  // Une demande acceptee vaut autorisation : l'autre a repondu, il a
  // choisi de parler a cette personne.
  if (record.opened) return;

  const friends = await readKeys(paths.friends(uid));
  if (!friends.includes(other)) {
    throw new SiroccoDataError(
      t.messages.erreurs.plusAmis,
      'not-friends',
    );
  }
}

/**
 * Compose des conversations completes a partir de leurs enregistrements.
 * Les fils, les horodatages de lecture et les profils partent en parallele
 * pour toute la liste : dix conversations font une vague de lectures, pas
 * dix allers-retours en file.
 */
async function buildConversations(
  rows: { id: string; record: ConversationRecord }[],
  uid: string,
): Promise<Conversation[]> {
  if (rows.length === 0) return [];

  const [threads, reads, friendIds, fenetres] = await Promise.all([
    Promise.all(
      rows.map((row) =>
        readList<MessageRecord>(paths.messages(row.id), {
          orderBy: 'createdAt',
          limit: APERCU,
        }),
      ),
    ),
    Promise.all(rows.map((row) => readLastRead(row.id, keysOf(row.record.members)))),
    readKeys(paths.friends(uid)),
    /*
      La fenetre de co-presence : sept jours pour s'ecrire librement apres
      s'etre vus en vrai. L'ecran doit la connaitre, sinon il fermerait le
      composeur sur une conversation que les regles autorisent.
    */
    Promise.all(rows.map((row) => readOptionalValue<number>(paths.coPresence(row.id)))),
  ]);
  const friends = new Set(friendIds);
  const users = await loadUsers(rows.flatMap((row) => keysOf(row.record.members)));

  return rows.map((row, index) => {
    const { record } = row;
    const members = keysOf(record.members);
    const profiles = members.map((member) => users.get(member) ?? unknownUser(member));
    const thread = threads[index];
    const seen = reads[index];
    const seenAt = seen.get(uid) ?? 0;
    const last = thread.at(-1);

    const other = profiles.find((member) => member.id !== uid);

    return {
      id: row.id,
      serverId: record.serverId,
      type: record.type,
      // Le titre d'une directe depend de qui regarde : c'est le pseudo de
      // l'autre, jamais un nom fige a la creation.
      title:
        record.type === 'direct' ? (other?.username ?? record.title ?? '') : (record.title ?? ''),
      // Meme logique pour la photo : celle du groupe, ou le visage d'en face.
      avatarUrl: record.type === 'direct' ? (other?.avatarUrl ?? null) : (record.avatarUrl ?? null),
      description: record.description ?? '',
      members: profiles,
      adminId: record.adminId ?? null,
      eventId: record.eventId ?? null,
      asso: record.asso ?? null,
      /*
        Un groupe accueille qui il veut ; une directe demande une amitie,
        ou une demande de message qui a trouve sa reponse.
      */
      /*
        Une demande envoyee se ferme des que son unique message est parti :
        la suite depend de l'autre. Une demande recue, elle, s'ecrit — c'est
        precisement ce qui l'accepte.

        On regarde ce qui a ete ecrit *depuis* l'ouverture, pas si le fil
        est vide : une demande peut naitre sur une conversation qui a une
        histoire, entre deux anciens amis. Sans cette date, elle se serait
        fermee avant d'avoir servi.
      */
      /* Appeler ne se donne qu'entre amis : voir le type partage. */
      ami: Boolean(other && friends.has(other.id)),
      canWrite:
        // On s'est vus il y a moins d'une semaine : on s'ecrit, point.
        (fenetres[index] ?? 0) > Date.now() ||
        (record.request
          ? record.request !== uid ||
            !thread.some(
              (message) => message.senderId === uid && message.createdAt >= (record.requestAt ?? 0),
            )
          : record.type === 'group' ||
            Boolean(other && friends.has(other.id)) ||
            Boolean(record.opened)),
      request: record.request ? (record.request === uid ? 'sent' : 'received') : null,
      lastMessage: last ? toMessage(row.id, last, seen, members) : null,
      /*
        Le repere de lecture ne s'ecrit qu'a la date d'un message, lu ou
        envoye : il survit a l'effacement des messages et dit qu'il y en a eu.
      */
      dejaEcrit: [...seen.values()].some((date) => date > 0),
      // Non-lus : ce que les autres ont poste depuis mon dernier passage.
      // Un message retire ne compte pas : il n'y a plus rien a lire. Le
      // compte s'arrete a la fenetre lue — voir `APERCU`.
      unreadCount: countUnreadIn(thread, uid, seenAt),
      supprimeNonLu: Boolean(
        last && last.deleted && last.senderId !== uid && last.createdAt > seenAt,
      ),
      muted: Boolean(record.mutedBy?.[uid]),
      createdAt: toIso(record.createdAt),
    };
  });
}

/**
 * Ce qui merite une ligne dans la liste, meme sans un mot echange.
 *
 * Trois cas. Un groupe : on vient de le creer, il faut pouvoir y revenir.
 * Un fil qui porte des messages, evidemment. Et le tete-a-tete de deux
 * amis, vide : il est la pour inviter a commencer, c'est tout son role.
 *
 * Reste ce qu'on ecarte : le fil qu'on cree en ouvrant le profil d'un
 * inconnu et en appuyant sur « lui ecrire » sans rien dire. Il
 * apparaissait chez les deux, « Envoyez un premier message ! » en gris,
 * comme si quelque chose s'etait passe.
 */
function vivante(conversation: Conversation, uid: string, amis: Set<string>): boolean {
  if (conversation.type === 'group' || conversation.lastMessage !== null) return true;
  return conversation.members.some((membre) => membre.id !== uid && amis.has(membre.id));
}

/**
 * Ouvre le tete-a-tete de deux amis, s'il n'existe pas deja.
 *
 * Appelee quand une amitie se noue : la conversation apparait alors dans
 * les deux listes, vide, et invite a dire quelque chose. Elle ne porte
 * aucune demande — entre amis, la porte est ouverte.
 */
export async function ensureDirect(otherUid: string, serverId: string): Promise<void> {
  const uid = requireUid();
  if (otherUid === uid) return;

  const connue = await readOptionalValue<string>(paths.directConversation(uid, otherUid));
  if (connue && (await readOptionalValue(paths.conversation(connue)))) {
    // Elle existe : on s'assure seulement qu'elle figure chez les deux.
    await updateValues({
      [paths.userConversation(uid, connue)]: true,
      [paths.userConversation(otherUid, connue)]: true,
    });
    return;
  }

  const id = newKey(paths.conversations());
  await updateValues({
    [paths.conversation(id)]: {
      serverId,
      type: 'direct',
      title: '',
      description: '',
      adminId: null,
      eventId: null,
      createdAt: Date.now(),
      members: membersMap([uid, otherUid]),
    } satisfies ConversationRecord,
    [paths.directConversation(uid, otherUid)]: id,
    [paths.userConversation(uid, id)]: true,
    [paths.userConversation(otherUid, id)]: true,
  });
}

/** Date qui classe une conversation : son dernier message, sinon sa creation. */
function sortKey(conversation: Conversation): number {
  return Date.parse(conversation.lastMessage?.createdAt ?? conversation.createdAt);
}

/**
 * Mes conversations, la plus vivante en tete.
 *
 * Toutes destinations confondues : une amitie nouee a Marrakech ne
 * s'interrompt pas parce qu'on ouvre Londres, et retrouver un fil ne doit
 * pas obliger a se rappeler dans quelle ville on l'a commence. Le spot
 * reste inscrit sur la conversation — il dit d'ou elle vient — mais il ne
 * filtre plus rien.
 *
 * On part de `userConversations/{uid}` : c'est le seul index qui dit ce
 * qui m'appartient, et quitter un groupe suffit a l'en retirer.
 */
export async function listConversations(): Promise<Conversation[]> {
  const uid = requireUid();
  const [ids, blocked] = await Promise.all([readKeys(paths.userConversations(uid)), listBlocked()]);
  if (ids.length === 0) return [];

  const muted = new Set(blocked);
  const records = await readMany<ConversationRecord>(paths.conversation, ids);
  const rows = ids
    .map((id) => ({ id, record: records.get(id) }))
    .filter((row): row is { id: string; record: ConversationRecord } =>
      Boolean(row.record && row.record.members?.[uid]),
    )
    // Bloquer quelqu'un, c'est ne plus le croiser : sa discussion privee
    // quitte la liste. Le fil reste en base, il reviendra au deblocage.
    .filter(
      (row) =>
        row.record.type !== 'direct' ||
        !keysOf(row.record.members).some((member) => member !== uid && muted.has(member)),
    );

  const conversations = await buildConversations(rows, uid);
  const amis = new Set(await readKeys(paths.friends(uid)));
  /*
    Une demande recue n'est pas encore une conversation : elle attend
    dans son propre ecran. Celle qu'on a envoyee, si — c'est notre
    message, il doit rester quelque part, et l'ecran des demandes ne
    regarde que ce qui appelle une reponse de nous.
  */
  return conversations
    .filter((conversation) => conversation.request !== 'received')
    .filter((conversation) => vivante(conversation, uid, amis))
    .sort((a, b) => sortKey(b) - sortKey(a));
}

/**
 * Les demandes de message recues.
 *
 * Elles vivent a part de la liste principale tant qu'on n'a pas repondu :
 * recevoir un mot de quelqu'un qu'on ne connait pas ne doit pas bousculer
 * ses conversations. Celles qu'on envoie n'y sont pas — cet ecran est une
 * boite d'entree, pas un registre.
 */
export async function listRequests(): Promise<Conversation[]> {
  const uid = requireUid();
  const [ids, blocked] = await Promise.all([readKeys(paths.userConversations(uid)), listBlocked()]);
  if (ids.length === 0) return [];

  const muted = new Set(blocked);
  const records = await readMany<ConversationRecord>(paths.conversation, ids);
  const rows = ids
    .map((id) => ({ id, record: records.get(id) }))
    .filter((row): row is { id: string; record: ConversationRecord } =>
      Boolean(row.record?.request && row.record.request !== uid && row.record.members?.[uid]),
    )
    .filter((row) => !keysOf(row.record.members).some((m) => m !== uid && muted.has(m)));

  const conversations = await buildConversations(rows, uid);
  // Une demande dont le message n'est jamais parti n'a rien a demander.
  return conversations
    .filter((conversation) => conversation.lastMessage !== null)
    .sort((a, b) => sortKey(b) - sortKey(a));
}

/**
 * Combien de demandes je n'ai pas encore vues.
 *
 * « Vues » et non « traitees » : ouvrir l'ecran suffit. Une pastille dit
 * qu'il y a quelque chose de neuf derriere, pas qu'il reste du travail.
 *
 * Volontairement pauvre — ni profils, ni fils : elle est relue a chaque
 * changement d'onglet, la ou `listRequests` compose des conversations
 * entieres pour les afficher.
 */
export async function countRequests(): Promise<number> {
  const uid = requireUid();
  const [ids, vuA] = await Promise.all([
    readKeys(paths.userConversations(uid)),
    readOptionalValue<number>(`${paths.user(uid)}/seenMessageRequestsAt`),
  ]);
  if (ids.length === 0) return 0;

  const depuis = vuA ?? 0;
  const records = await readMany<ConversationRecord>(paths.conversation, ids);
  const candidates = ids.filter((id) => {
    const record = records.get(id);
    if (!record?.request || record.request === uid || !record.members?.[uid]) return false;
    return (record.requestAt ?? record.createdAt ?? 0) > depuis;
  });
  if (candidates.length === 0) return 0;

  /*
    Une demande dont le message n'est jamais parti ne compte pas : ouvrir
    le profil de quelqu'un et appuyer sur « lui ecrire » cree deja la
    conversation, sans qu'un mot ait ete dit. Un seul message est lu par
    candidat, et il n'y en a qu'une poignee.
  */
  const fils = await Promise.all(
    candidates.map((id) =>
      readList<MessageRecord>(paths.messages(id), { orderBy: 'createdAt', limit: 1 }),
    ),
  );
  return fils.filter((thread) => thread.length > 0).length;
}

/** L'ecran a ete ouvert : ce qui s'y trouvait est vu. */
export async function markRequestsSeen(): Promise<void> {
  const uid = requireUid();
  await writeValue(`${paths.user(uid)}/seenMessageRequestsAt`, Date.now());
}

/* --- Ce qui arrive pendant qu'on regarde ---------------------------- */

/**
 * Suit le fil ouvert, message par message.
 *
 * La premiere version relisait **tout le fil** a chaque remuement. C'est
 * juste, et c'est la depense la plus lourde de l'application : dans une
 * conversation vivante, chaque message renvoyait l'integralite de
 * l'historique aux deux interlocuteurs. Une discussion de cinq cents
 * messages telechargeait cinq cents messages pour en montrer un.
 *
 * Elle avait de plus un angle mort : la sonnette etait posee sur le
 * dernier message par horodatage, or celui-ci est pose par l'horloge de
 * celui qui ecrit. Un telephone en retard produisait un message qui ne
 * devenait jamais « le dernier », donc qui ne sonnait pas.
 *
 * Trois ecoutes remplacent la relecture, toutes bornees a la conversation
 * ouverte, et toutes coupees au demontage :
 *
 * - `onChildAdded` a partir du dernier message connu : ce qui arrive
 *   s'ajoute, et rien d'autre ne transite ;
 * - `onChildChanged` : un message efface ou modifie se remplace sur
 *   place, sans quoi il resterait affiche tel qu'il etait ;
 * - `lastRead` : les accuses de lecture se recalculent **en memoire**,
 *   sans rien relire — c'est un booleen par message, pas une donnee.
 */
export function watchThread(
  conversationId: string,
  /**
   * Le dernier message deja affiche, qui sert de borne.
   *
   * Il vient de l'ecran plutot que d'une lecture a nous : c'est une
   * requete de moins, et surtout aucun trou possible entre ce qui est
   * montre et ce que l'on se met a ecouter.
   */
  depuis: Message | null,
  onChange: (maj: (precedent: Message[]) => Message[]) => void,
): () => void {
  const uid = requireUid();
  const arrets: Array<() => void> = [];
  let vivant = true;

  /* L'etat de lecture, garde ici pour pouvoir recalculer sans relire. */
  let seen = new Map<string, number>();
  let membres: string[] = [];

  void (async () => {
    const record = await requireMembership(conversationId, uid);
    if (!vivant) return;
    membres = keysOf(record.members);
    seen = await readLastRead(conversationId, membres);
    if (!vivant) return;

    /*
      Sans borne, `onChildAdded` rejouerait le fil entier — ce qu'on
      cherchait justement a eviter.
    */
    const apres = depuis
      ? query(
          ref(db(), paths.messages(conversationId)),
          orderByChild('createdAt'),
          startAfter(Date.parse(depuis.createdAt), depuis.id),
        )
      : query(ref(db(), paths.messages(conversationId)), orderByChild('createdAt'));

    arrets.push(
      onChildAdded(apres, (snap) => {
        const record = { ...(snap.val() as MessageRecord), id: snap.key! };
        const message = toMessage(conversationId, record, seen, membres);

        onChange((precedent) =>
          /* Le fil charge peut deja le porter : entre la lecture initiale
             et la pose de l'ecoute, un message a pu se glisser. */
          precedent.some((ligne) => ligne.id === message.id)
            ? precedent
            : [...precedent, message],
        );

        /*
          Lire, c'est eteindre. L'ecran est ouvert sous les yeux du
          membre : laisser le compteur de non-lus monter reviendrait a lui
          annoncer un message qu'il est en train de lire.
        */
        if (record.senderId !== uid) {
          void updateValues({
            [paths.lastRead(conversationId, uid)]: record.createdAt,
            [paths.unreadOf(uid, conversationId)]: null,
          }).catch(() => undefined);
        }
      }),
    );

    arrets.push(
      onChildChanged(ref(db(), paths.messages(conversationId)), (snap) => {
        const record = { ...(snap.val() as MessageRecord), id: snap.key! };
        const message = toMessage(conversationId, record, seen, membres);
        onChange((precedent) =>
          precedent.map((ligne) => (ligne.id === message.id ? message : ligne)),
        );
      }),
    );

    arrets.push(
      onValue(ref(db(), paths.lastReadOf(conversationId)), (snap) => {
        seen = new Map(Object.entries((snap.val() as Record<string, number> | null) ?? {}));

        /*
          Recalcul local : aucun message ne repasse par le reseau.

          **On ne rend le meme fil que s'il a vraiment change.** Cette
          ecoute part une premiere fois des qu'on la branche, avec l'etat
          courant — le meme que celui deja calcule au chargement. Fabriquer
          malgre tout un nouveau tableau relancait le rendu, et l'ecran se
          recollait au dernier message une fraction de seconde apres son
          ouverture : le fil sautait de quelques pixels sous les yeux.
        */
        onChange((precedent) => {
          let change = false;
          const suivant = precedent.map((ligne) => {
            const lu = membres.every(
              (membre) =>
                membre === ligne.senderId ||
                (seen.get(membre) ?? 0) >= Date.parse(ligne.createdAt),
            );
            if (lu === ligne.read) return ligne;
            change = true;
            return { ...ligne, read: lu };
          });
          return change ? suivant : precedent;
        });
      }),
    );
  })();

  return () => {
    vivant = false;
    for (const arret of arrets) arret();
  };
}

/**
 * Suit la liste des conversations en direct, sans l'ecouter fil par fil.
 *
 * La version precedente posait une ecoute sur l'index **et une par
 * conversation** — le dernier message de chacune, pour savoir laquelle
 * remuait. Cela marche, et cela ne tient pas : le nombre d'ecoutes suit
 * le nombre de fils, et chaque message declenchait une relecture
 * complete de toute la messagerie.
 *
 * Deux petites branches disent la meme chose :
 *
 * - `userConversations/{uid}`, des cles nues : un fil qui apparait ou
 *   disparait ;
 * - `users/{uid}/nonLus`, des nombres : un message qui arrive, puisque
 *   c'est la fonction qui le recoit qui tient ce compteur, et l'ouverture
 *   d'un fil qui l'eteint.
 *
 * Ce qu'on n'attrape plus : un message que **j'envoie** depuis un autre
 * appareil, et un message efface par son auteur. Les deux ne changent
 * qu'un apercu, et l'ecran se recompose de toute facon en y revenant.
 *
 * Le delai groupe les rafales : envoyer touche l'index et le compteur,
 * ce qui ferait deux relectures pour un seul message.
 */
function watchMesFils(uid: string, onChange: () => void, delai = 250): () => void {
  let attente: ReturnType<typeof setTimeout> | null = null;
  let vivant = true;

  function relire() {
    if (attente) clearTimeout(attente);
    attente = setTimeout(() => {
      if (vivant) onChange();
    }, delai);
  }

  const arretIndex = onValue(ref(db(), paths.userConversations(uid)), relire);
  const arretNonLus = onValue(ref(db(), paths.unread(uid)), relire);

  return () => {
    vivant = false;
    if (attente) clearTimeout(attente);
    arretIndex();
    arretNonLus();
  };
}

export function watchConversations(onChange: (list: Conversation[]) => void): () => void {
  const uid = requireUid();
  let vivant = true;

  const arret = watchMesFils(uid, () => {
    void listConversations()
      .then((liste) => vivant && onChange(liste))
      .catch(() => undefined);
  });

  return () => {
    vivant = false;
    arret();
  };
}

/**
 * Les messages qui m'attendent, en direct.
 *
 * Une seule branche, quelques nombres : c'est tout ce que la barre
 * d'onglets telecharge desormais. Elle ecoutait auparavant l'index de mes
 * conversations et le dernier message de chacune, puis relisait
 * l'ensemble des conversations a chaque declenchement — trois passes
 * completes sur toute la messagerie pour un seul nombre, meme quand le
 * membre lisait le fil d'actualite.
 *
 * Le compteur est tenu par la Cloud Function qui recoit chaque message ;
 * ouvrir un fil l'eteint.
 */
export function watchNonLus(onChange: (total: number) => void): () => void {
  return watchNonLusParFil((parFil) =>
    onChange(Object.values(parFil).reduce((total, compte) => total + (compte > 0 ? compte : 0), 0)),
  );
}

/**
 * Les memes compteurs, fil par fil.
 *
 * La banniere des messages s'en sert : un compteur qui monte veut dire
 * qu'un message est arrive dans ce fil-la. Aucune lecture de plus que
 * pour la pastille — c'est la meme branche, lue une fois.
 */
export function watchNonLusParFil(
  onChange: (parFil: Record<string, number>) => void,
): () => void {
  const uid = requireUid();
  return onValue(ref(db(), paths.unread(uid)), (snap) => {
    onChange((snap.val() as Record<string, number> | null) ?? {});
  });
}


/**
 * Ce qui reste a lire dans la fenetre chargee.
 * Elle est prise a la fin du fil : tout ce qui est arrive depuis mon
 * dernier passage s'y trouve, sauf a avoir laisse filer plus de trente
 * messages — le compte plafonne alors, sans jamais mentir a la baisse.
 */
function countUnreadIn(thread: MessageRecord[], uid: string, seenAt: number): number {
  return thread.filter(
    (message) =>
      message.senderId !== uid &&
      message.createdAt > seenAt &&
      !message.deleted &&
      /*
        Les lignes de service ne comptent pas. Un depart de groupe, une
        amitie rompue : ce sont des faits, pas des mots qui attendent une
        reponse. Comptees, elles allumaient une pastille de non-lu et un
        nombre sur l'icone de l'application — une notification, exactement
        ce qu'elles ne doivent pas declencher.
      */
      !message.systeme,
  ).length;
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  const [conversation] = await buildConversations([{ id: conversationId, record }], uid);
  return conversation;
}

/**
 * Le fil complet, du plus ancien au plus recent.
 *
 * Celui-la n'est pas rogne, contrairement aux apercus de la liste : on
 * l'ouvre pour le lire, et remonter dans une discussion est un usage,
 * pas un cas limite. Un fil de texte pese quelques dizaines de kilo-
 * octets ; ce qui l'alourdissait, ce sont les photos et les vocaux, et
 * ceux-la sortent de la base pour Storage.
 *
 * Ouvrir un fil vaut lecture : on cale le repere sur le dernier message
 * pour que la pastille de non-lus s'eteigne des le retour a la liste.
 */
/**
 * Les reperes de lecture, tels quels : qui a ouvert le fil, et quand.
 *
 * La feuille « Infos » d'un message s'en sert : un message est lu par un
 * membre si son repere depasse l'instant d'envoi — et le repere lui-meme
 * dit quand. C'est l'heure d'ouverture du fil, pas celle du regard sur
 * la bulle precise : la messagerie ne sait rien de plus fin, et c'est
 * deja ce que la coche « lu » affirme.
 */
export async function lireReperes(conversationId: string): Promise<Record<string, number>> {
  return (await readValue<Record<string, number>>(paths.lastReadOf(conversationId))) ?? {};
}

/** Un message ecrit, tel que la recherche des discussions le parcourt. */
export interface MessageEcrit {
  conversationId: string;
  id: string;
  senderId: string;
  content: string;
  createdAt: string;
}

/**
 * Ce qui a ete ecrit dans mes fils, pour la recherche des discussions.
 *
 * La Realtime Database ne cherche pas dans du texte : on lit donc les
 * fils et l'on cherche sur le telephone. C'est peu — mesure le 26
 * septembre 2026, le compte le plus bavard tient en quatre-vingts
 * messages et quinze kilooctets.
 *
 * Une lecture seule, sans `listThread` : chercher un mot ne doit pas
 * marquer les fils comme lus, ni eteindre leurs pastilles. Seul le texte
 * ecrit passe — ni vocal, ni photo sans legende, ni trace d'appel, ni
 * message retire, ni ce que le fil note de lui-meme.
 */
export async function lireLesTextes(conversationIds: string[]): Promise<MessageEcrit[]> {
  const fils = await Promise.all(
    conversationIds.map((conversationId) =>
      readList<MessageRecord>(paths.messages(conversationId), { orderBy: 'createdAt' })
        /* Un fil qu'on ne peut plus lire ne doit pas emporter les autres. */
        .catch(() => [])
        .then((fil) =>
          fil
            .filter((message) => message.content && !message.deleted && !message.systeme)
            .map((message) => ({
              conversationId,
              id: message.id,
              senderId: message.senderId,
              content: message.content,
              createdAt: toIso(message.createdAt),
            })),
        ),
    ),
  );
  return fils.flat();
}

export async function listThread(conversationId: string): Promise<Message[]> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  const members = keysOf(record.members);

  const [thread, seen] = await Promise.all([
    readList<MessageRecord>(paths.messages(conversationId), { orderBy: 'createdAt' }),
    readLastRead(conversationId, members),
  ]);

  const last = thread.at(-1);
  if (last && (seen.get(uid) ?? 0) < last.createdAt) {
    seen.set(uid, last.createdAt);
    /*
      Le repere de lecture et le compteur de non-lus tombent ensemble :
      c'est le serveur qui incremente le second, mais l'eteindre est le
      geste de celui qui lit, et il n'a pas a attendre une fonction.
    */
    await updateValues({
      [paths.lastRead(conversationId, uid)]: last.createdAt,
      [paths.unreadOf(uid, conversationId)]: null,
    });
  } else {
    /*
      Ouvrir un fil l'eteint, meme quand le repere de lecture etait deja
      a jour. Un compteur peut rester allume sans message pour le
      justifier — un message efface depuis, ou une pastille rallumee par
      un declencheur en retard — et sans ce geste rien ne l'eteindrait
      jamais : le fil s'ouvre, il n'y a rien de neuf, la pastille reste.
    */
    await removeValue(paths.unreadOf(uid, conversationId)).catch(() => undefined);
  }

  return thread.map((message) => toMessage(conversationId, message, seen, members));
}

/**
 * Le rapport largeur/hauteur d'une image, avant de l'envoyer.
 *
 * On le mesure ici, sur la data URL que l'ecran a deja en main : c'est le
 * seul moment ou l'image est certainement disponible sans un aller-retour
 * de plus. Il sert a reserver la place de la bulle des le premier rendu,
 * pour qu'aucun fil ne s'allonge sous les yeux de qui le lit.
 */
async function mesurerRapport(source: string): Promise<number | null> {
  return new Promise((resoudre) => {
    const image = new Image();
    image.onload = () =>
      resoudre(image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : null);
    // Une mesure ratee n'empeche pas d'envoyer : on retombe sur le cadre
    // par defaut, comme pour les photos d'avant.
    image.onerror = () => resoudre(null);
    image.src = source;
  });
}

export async function sendMessage(
  conversationId: string,
  content: string,
  attachedEventId: string | null = null,
  imageUrl: string | null = null,
  voice: VoiceMessage | null = null,
  video: VideoAEnvoyer | null = null,
  attachedPostId: string | null = null,
  replyTo: string | null = null,
): Promise<Message> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  await requireFriendship(conversationId, record, uid);

  const trimmed = content.trim();
  // Une photo ou un vocal se suffisent a eux-memes : seul un message
  // entierement vide est refuse.
  if (!trimmed && !imageUrl && !attachedEventId && !attachedPostId && !voice && !video) {
    throw new SiroccoDataError(t.messages.erreurs.vide, 'empty-message', 'content');
  }

  /*
    Photo et vocal partent vers Storage avant d'ecrire le message. C'est
    le seul endroit ou ils passent : l'ecran n'a jamais manipule qu'une
    data URL, ce qui lui donne son apercu immediat et laisse un
    enregistrement annule sans rien laisser derriere lui.

    La video, elle, arrive en blob — elle est trop lourde pour supporter
    le detour par le base64 — et son image de couverture part avec, dans
    le meme lot : deux fichiers pour un message, mais le fil n'aura plus
    a telecharger le film pour montrer quelque chose.
  */
  const [photo, son, film, couverture, rapport] = await Promise.all([
    imageUrl ? uploadIfDataUrl(imageUrl) : null,
    voice ? uploadIfDataUrl(voice.audioUrl) : null,
    video ? uploadMedia(video.blob) : null,
    video?.poster ? uploadMedia(video.poster) : null,
    imageUrl ? mesurerRapport(imageUrl) : null,
  ]);

  const createdAt = Date.now();
  const message: MessageRecord = {
    senderId: uid,
    content: trimmed,
    createdAt,
    attachedEventId: attachedEventId ?? null,
    attachedPostId: attachedPostId ?? null,
    ...(replyTo ? { replyTo } : {}),
    imageUrl: photo,
    ...(rapport ? { imageRatio: rapport } : {}),
    ...(voice
      ? {
          audioUrl: son as string,
          audioDurationMs: voice.durationMs,
          audioPeaks: voice.peaks,
        }
      : {}),
    ...(video
      ? {
          videoUrl: film as string,
          videoDurationMs: video.durationMs,
          ...(couverture ? { videoPosterUrl: couverture } : {}),
        }
      : {}),
  };
  let id: string;
  try {
    id = await pushValue(paths.messages(conversationId), message);
  } catch (cause) {
    if (record.type === 'direct' && estUnRefus(cause)) throw injoignable();
    throw cause;
  }

  /*
    Repondre a une demande l'accepte. C'est le seul geste qui l'ouvre :
    ni la lire, ni la garder ne changent rien. La conversation rejoint
    alors la liste principale des deux cotes.
  */
  if (record.request && record.request !== uid) {
    await updateValues({
      [`${paths.conversation(conversationId)}/request`]: null,
      [`${paths.conversation(conversationId)}/requestSent`]: null,
      [`${paths.conversation(conversationId)}/opened`]: true,
    });
  } else if (record.request === uid) {
    /* Ma demande vient de partir : c'etait le seul message auquel j'avais
       droit, et les regles s'appuient sur ce drapeau pour le savoir. */
    await updateValues({
      [`${paths.conversation(conversationId)}/requestSent`]: true,
    });
  }

  // Envoyer vaut lecture : sans cela mon propre message reviendrait en non-lu.
  await updateValues({
    [paths.lastRead(conversationId, uid)]: createdAt,
    [paths.unreadOf(uid, conversationId)]: null,
  });

  return toMessage(
    conversationId,
    { ...message, id },
    new Map([[uid, createdAt]]),
    keysOf(record.members),
  );
}

/**
 * Efface un message, pour tout le monde.
 *
 * Reserve a son auteur, cote application comme cote regles. Il n'y a pas
 * de "supprimer pour moi seul" : cela demanderait de tenir la liste des
 * messages masques de chaque membre, pour un geste que personne ne
 * reclame encore.
 */
/**
 * Retire un message, en laissant une trace.
 *
 * L'enregistrement n'est pas efface mais vide : sans lui, le message
 * disparaissait du fil de l'autre sans un mot, et il ne restait qu'un
 * trou entre deux repliques. La trace garde l'expediteur et l'horodatage,
 * de quoi rester a sa place dans la conversation.
 */
export async function deleteMessage(conversationId: string, messageId: string): Promise<void> {
  const uid = requireUid();
  await requireMembership(conversationId, uid);

  const record = await readValue<MessageRecord>(paths.message(conversationId, messageId));
  if (!record) return;
  if (record.senderId !== uid) {
    throw new SiroccoDataError(t.messages.erreurs.effacerLesSiens, 'forbidden');
  }

  const tombstone: MessageRecord = {
    senderId: uid,
    content: '',
    createdAt: record.createdAt,
    attachedEventId: null,
    imageUrl: null,
    deleted: true,
  };
  await writeValue(paths.message(conversationId, messageId), tombstone);
}

/**
 * Corrige le texte d'un message, pour tout le monde.
 *
 * Reserve a son auteur, cote application comme cote regles : la
 * validation de `messages` exige que l'expediteur soit celui qui ecrit,
 * et une correction ne change pas l'expediteur. Seul le texte change ;
 * l'heure d'envoi reste, et la date de correction s'ajoute. Quinze
 * minutes apres l'envoi, les regles refusent : on le dit avant d'essayer.
 */
export async function editMessage(
  conversationId: string,
  messageId: string,
  content: string,
): Promise<void> {
  const uid = requireUid();
  await requireMembership(conversationId, uid);

  const record = await readValue<MessageRecord>(paths.message(conversationId, messageId));
  if (!record || record.deleted) return;
  if (record.senderId !== uid) {
    throw new SiroccoDataError(t.messages.erreurs.modifierLesSiens, 'forbidden');
  }
  if (Date.now() - record.createdAt > DELAI_DE_MODIFICATION_MS) {
    throw new SiroccoDataError(t.messages.erreurs.tropTardPourModifier, 'too-late');
  }

  try {
    await updateValues({
      [`${paths.message(conversationId, messageId)}/content`]: content,
      [`${paths.message(conversationId, messageId)}/editedAt`]: Date.now(),
    });
  } catch (cause) {
    /*
      L'heure du telephone n'est pas celle du serveur : a la limite, la
      regle peut refuser ce que l'horloge locale permettait encore.
    */
    if (String((cause as { code?: string })?.code ?? cause).toLowerCase().includes('permission')) {
      throw new SiroccoDataError(t.messages.erreurs.tropTardPourModifier, 'too-late');
    }
    throw cause;
  }
}

/**
 * Retire une conversation de ma liste.
 *
 * Le fil n'est pas detruit : il appartient aussi a l'autre, et l'effacer
 * chez lui serait decider a sa place. Quitter un groupe passe par
 * `leaveGroup`, qui retire en plus mon nom de ses membres.
 */
export async function deleteConversation(
  conversationId: string,
  successorId?: string,
): Promise<void> {
  const uid = requireUid();
  const record = await readValue<ConversationRecord>(paths.conversation(conversationId));

  if (record?.type === 'group' && record.members?.[uid]) {
    await leaveGroup(conversationId, successorId);
    return;
  }
  await removeValue(paths.userConversation(uid, conversationId));
}

/**
 * Renomme un groupe.
 *
 * Ouvert a tous ses membres, contrairement a la photo : un nom se corrige
 * souvent — une faute de frappe, un groupe qui change d'objet — et
 * dependre de l'admin pour ca bloquerait tout le monde des qu'il n'est
 * plus la. Les regles le permettent deja : un membre peut ecrire dans la
 * conversation ou il figure.
 */
export async function renameGroup(conversationId: string, title: string): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(t.messages.erreurs.priveSansNom, 'not-a-group');
  }

  if (record.adminId && record.adminId !== uid) {
    throw new SiroccoDataError(t.messages.erreurs.renommerAdmin, 'not-admin');
  }

  const clean = title.trim().slice(0, 60);
  if (!clean) throw new SiroccoDataError(t.messages.erreurs.nomDuGroupe, 'invalid-title', 'title');

  await writeValue(`${paths.conversation(conversationId)}/title`, clean);
  const [conversation] = await buildConversations(
    [{ id: conversationId, record: { ...record, title: clean } }],
    uid,
  );
  return conversation;
}

/**
 * La description d'un groupe, changee par son administrateur.
 *
 * Le meme chemin que le nom, la meme reserve : les regles portent une
 * validation qui refuse l'ecriture a qui n'est pas l'administrateur. Le
 * verifier aussi ici n'est pas une redondance — cela rend une phrase en
 * francais la ou la base ne rendrait qu'un refus opaque.
 */
export async function describeGroup(
  conversationId: string,
  description: string,
): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(t.messages.erreurs.priveSansDescription, 'not-a-group');
  }
  if (record.adminId && record.adminId !== uid) {
    throw new SiroccoDataError(
      t.messages.erreurs.descriptionAdmin,
      'not-admin',
    );
  }

  const clean = description.trim().slice(0, 200);
  await writeValue(`${paths.conversation(conversationId)}/description`, clean || null);
  const [conversation] = await buildConversations(
    [{ id: conversationId, record: { ...record, description: clean } }],
    uid,
  );
  return conversation;
}


/**
 * Passe la main : le membre designe devient administrateur.
 *
 * Un groupe n'a qu'un administrateur, donc nommer quelqu'un revient a
 * cesser de l'etre. C'est voulu — deux personnes qui peuvent s'exclure
 * mutuellement finissent par le faire.
 */
export async function setGroupAdmin(
  conversationId: string,
  memberId: string,
): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(t.messages.erreurs.priveSansAdmin, 'not-a-group');
  }
  if (record.adminId !== uid) {
    throw new SiroccoDataError(t.messages.erreurs.passerAdmin, 'forbidden');
  }
  if (!record.members?.[memberId]) {
    throw new SiroccoDataError(t.messages.erreurs.plusDansLeGroupe, 'not-a-member');
  }

  await writeValue(`${paths.conversation(conversationId)}/adminId`, memberId);
  const [conversation] = await buildConversations(
    [{ id: conversationId, record: { ...record, adminId: memberId } }],
    uid,
  );
  return conversation;
}

/**
 * Retire quelqu'un du groupe.
 *
 * Reserve a l'administrateur, et jamais sur lui-meme : partir de son
 * propre groupe se fait par « Quitter le groupe », qui pose la question
 * avant. Sans ce garde-fou, un geste malheureux laisserait un groupe sans
 * personne pour l'administrer.
 */
export async function removeGroupMember(conversationId: string, memberId: string): Promise<void> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(t.messages.erreurs.priveSansMembres, 'not-a-group');
  }
  if (record.adminId !== uid) {
    throw new SiroccoDataError(t.messages.erreurs.retirerAdmin, 'forbidden');
  }
  if (memberId === uid) {
    throw new SiroccoDataError(t.messages.erreurs.utiliseQuitter, 'forbidden');
  }

  /*
    Le mot avant le geste, comme pour un depart. C'est l'admin qui l'ecrit
    — il est membre, la regle le lui permet — et la ligne nomme celui qui
    s'en va, pas lui.
  */
  await noterMouvementDeGroupe(conversationId, uid, memberId, 'retire-groupe').catch(
    () => undefined,
  );

  await updateValues({
    [paths.conversationMember(conversationId, memberId)]: null,
    [paths.userConversation(memberId, conversationId)]: null,
  });
}

/** Photo du groupe. Reservee a son admin, comme le reste de sa fiche. */
export async function setGroupPhoto(
  conversationId: string,
  avatarUrl: string | null,
): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(t.messages.erreurs.priveSansPhoto, 'not-a-group');
  }
  if (record.adminId && record.adminId !== uid) {
    throw new SiroccoDataError(t.messages.erreurs.photoAdmin, 'forbidden');
  }

  const photo = avatarUrl ? await uploadIfDataUrl(avatarUrl) : null;
  await writeValue(`${paths.conversation(conversationId)}/avatarUrl`, photo);
  const [conversation] = await buildConversations(
    [{ id: conversationId, record: { ...record, avatarUrl: photo } }],
    uid,
  );
  return conversation;
}

/**
 * Signale une conversation a la moderation.
 *
 * Rien n'en disparait pour autant, a la difference d'un post : une
 * conversation se quitte deja par la suppression, et effacer d'office un
 * fil qu'on signale ferait perdre ce qu'on veut faire examiner.
 */
export async function reportConversation(
  conversationId: string,
  reason: ReportReason,
  details = '',
): Promise<string> {
  const reporterId = requireUid();
  // L'appartenance est verifiee avant d'ecrire : on ne signale pas un fil
  // dont on ne fait pas partie.
  await requireMembership(conversationId, reporterId);
  const reportId = newKey(paths.reports());

  await writeValue(`${paths.reports()}/${reportId}`, {
    type: 'conversation',
    conversationId,
    reporterId,
    reason,
    details: details.trim(),
    createdAt: Date.now(),
  });
  return reportId;
}

export async function toggleMute(conversationId: string): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  const muted = Boolean(record.mutedBy?.[uid]);

  if (muted) await removeValue(mutedByPath(conversationId, uid));
  else await writeValue(mutedByPath(conversationId, uid), true);

  const mutedBy = { ...record.mutedBy };
  if (muted) delete mutedBy[uid];
  else mutedBy[uid] = true;

  const [conversation] = await buildConversations(
    [{ id: conversationId, record: { ...record, mutedBy } }],
    uid,
  );
  return conversation;
}

/**
 * Ouvre la conversation privee avec quelqu'un, en la creant au besoin.
 * On cherche d'abord une directe existante, quel que soit le spot : deux
 * fils entre les memes personnes rendraient l'historique introuvable.
 */
export async function openDirect(otherUid: string, serverId: string): Promise<Conversation> {
  const uid = requireUid();
  if (otherUid === uid) {
    throw new SiroccoDataError(t.messages.erreurs.soiMeme, 'self-conversation');
  }
  if ((await listBlocked()).includes(otherUid)) {
    throw new SiroccoDataError(
      t.messages.erreurs.bloque,
      'blocked',
    );
  }
  /*
    Ecrire a quelqu'un qu'on ne connait pas ne se refuse plus : cela ouvre
    une demande. Un seul message part, et la conversation ne s'ouvre que
    si l'autre repond — c'est ce qui separe une prise de contact d'un
    harcelement. Elle n'apparait chez lui que dans l'ecran des demandes.

    L'organisateur d'un evenement n'a plus de porte a part. Elle avait un
    defaut de naissance : elle ouvrait bien le champ de saisie, mais
    l'envoi, lui, n'a jamais regarde autre chose que l'amitie — on ecrivait
    dans le vide et le message etait refuse. La demande fait desormais ce
    travail, pour lui comme pour tout le monde, et sans exception a tenir.
  */
  const friends = await readKeys(paths.friends(uid));
  const demande = !friends.includes(otherUid);

  /*
    Le fil existant, retrouve par la paire plutot qu'en fouillant sa
    propre liste. La difference compte : retirer une conversation de sa
    liste efface son entree d'index, et la recherche ne la trouvait donc
    plus — on en creait une seconde, et l'autre se retrouvait avec deux
    fils pour la meme personne. L'index de paire, lui, ne depend de la
    liste de personne.

    Le balayage reste en second : les conversations anterieures a cet
    index n'y figurent pas encore, et la premiere ouverture les y inscrit.
  */
  let existing: string | null =
    (await readOptionalValue<string>(paths.directConversation(uid, otherUid))) ?? null;
  let existingRecord = existing
    ? await readOptionalValue<ConversationRecord>(paths.conversation(existing))
    : null;
  if (!existingRecord) existing = null;

  if (!existing) {
    const ids = await readKeys(paths.userConversations(uid));
    const records = await readMany<ConversationRecord>(paths.conversation, ids);
    existing =
      ids.find((id) => {
        const candidat = records.get(id);
        return Boolean(
          candidat?.type === 'direct' && candidat.members?.[uid] && candidat.members?.[otherUid],
        );
      }) ?? null;
    existingRecord = existing ? (records.get(existing) ?? null) : null;
  }

  if (existing && existingRecord) {
    const record = existingRecord;

    /*
      Un fil qui existe deja mais que plus rien n'ouvre.

      C'est le cas de deux anciens amis : la conversation est la, avec son
      histoire, mais l'amitie a saute et rien ne la remplace. Sans cela,
      elle restait fermee a l'ecriture pour toujours — la demande de
      message, ouverte a tout le monde, ne l'atteignait pas puisqu'elle ne
      concernait que les fils a creer. On la rouvre donc comme on ouvre
      une porte a un inconnu : un message, et une reponse pour la suite.
    */
    if (demande && !record.request && !record.opened) {
      const requestAt = Date.now();
      await updateValues({
        [`${paths.conversation(existing)}/request`]: uid,
        [`${paths.conversation(existing)}/requestAt`]: requestAt,
        [`${paths.conversation(existing)}/requestSent`]: null,
      });
      record.request = uid;
      record.requestAt = requestAt;
    }

    /*
      Au passage, on repare : l'index de paire s'ecrit s'il manquait, et
      la conversation revient dans les listes des deux membres. Celui qui
      l'avait retiree de la sienne la retrouve donc plutot que d'en voir
      apparaitre une seconde a cote.
    */
    try {
      await updateValues({
        [paths.directConversation(uid, otherUid)]: existing,
        [paths.userConversation(uid, existing)]: true,
        [paths.userConversation(otherUid, existing)]: true,
      });
    } catch (cause) {
      // Il m'a bloque : les regles refusent de reposer le fil dans sa liste.
      if (estUnRefus(cause)) throw injoignable();
      throw cause;
    }

    const [conversation] = await buildConversations([{ id: existing, record }], uid);
    return conversation;
  }

  const id = newKey(paths.conversations());
  // Le titre reste vide : il se calcule a la lecture, sinon chacun verrait
  // son propre pseudo en tete de conversation.
  const record: ConversationRecord = {
    serverId,
    type: 'direct',
    title: '',
    description: '',
    adminId: null,
    createdAt: Date.now(),
    members: membersMap([uid, otherUid]),
    ...(demande ? { request: uid, requestAt: Date.now() } : {}),
  };

  try {
    await updateValues({
      [paths.conversation(id)]: record,
      [paths.directConversation(uid, otherUid)]: id,
      [paths.userConversation(uid, id)]: true,
      [paths.userConversation(otherUid, id)]: true,
    });
  } catch (cause) {
    // Il m'a bloque : les regles refusent de lui ouvrir un fil.
    if (estUnRefus(cause)) throw injoignable();
    throw cause;
  }

  const [conversation] = await buildConversations([{ id, record }], uid);
  return conversation;
}

export async function createGroup(
  serverId: string,
  title: string,
  description: string,
  memberIds: string[],
  avatarUrl: string | null = null,
  /** L'evenement, quand c'est sa conversation qu'on ouvre. */
  eventId: string | null = null,
): Promise<Conversation> {
  const uid = requireUid();
  const trimmed = title.trim();
  if (!trimmed) throw new SiroccoDataError(t.messages.erreurs.nomDuGroupe, 'empty-title', 'title');

  // Le createur est membre et admin, meme s'il ne s'est pas mis dans la liste.
  const members = [...new Set([uid, ...memberIds])];
  const id = newKey(paths.conversations());
  const record: ConversationRecord = {
    serverId,
    type: 'group',
    title: trimmed,
    description: description.trim(),
    avatarUrl: avatarUrl ? await uploadIfDataUrl(avatarUrl) : null,
    adminId: uid,
    createdAt: Date.now(),
    ...(eventId ? { eventId } : {}),
    members: membersMap(members),
  };

  // L'index de chaque membre est ecrit en meme temps que la conversation :
  // sans lui, le groupe n'apparaitrait dans la liste de personne.
  const patch: Record<string, unknown> = { [paths.conversation(id)]: record };
  for (const member of members) patch[paths.userConversation(member, id)] = true;
  await updateValues(patch);

  const [conversation] = await buildConversations([{ id, record }], uid);
  return conversation;
}

export async function addMembers(
  conversationId: string,
  memberIds: string[],
): Promise<Conversation> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(
      t.messages.erreurs.priveFermee,
      'not-a-group',
    );
  }

  /*
    Le groupe d'une sortie ne s'invite pas.

    On y entre en rejoignant l'evenement, et c'est vrai de l'organisateur
    comme des autres : la liste des presents est celle des inscrits, pas
    celle des invites. Les regles le refusent aussi — c'est la que la
    porte est reellement fermee ; ceci ne sert qu'a le dire en francais.
  */
  if (record.eventId) {
    throw new SiroccoDataError(
      t.messages.erreurs.groupeDeSortie,
      'groupe-evenement',
    );
  }
  /* Le groupe d'une association non plus : on y entre en la rejoignant. */
  if (record.asso) {
    throw new SiroccoDataError(t.messages.erreurs.groupeDAsso, 'groupe-asso');
  }

  /*
    On n'ajoute que ses amis.

    Jeter un inconnu dans une conversation n'est pas un service qu'on lui
    rend, et c'etait possible : la recherche portait sur tous les membres
    du spot.
  */
  const amis = await listFriends();
  const connus = new Set(amis.map((ami) => ami.id));
  const etranger = memberIds.find((member) => !connus.has(member) && !record.members?.[member]);
  if (etranger) {
    throw new SiroccoDataError(
      t.messages.erreurs.amisSeulement,
      'pas-ami',
    );
  }

  const added = [...new Set(memberIds)].filter((member) => !record.members?.[member]);
  if (added.length > 0) {
    const patch: Record<string, unknown> = {};
    for (const member of added) {
      patch[paths.conversationMember(conversationId, member)] = true;
      patch[paths.userConversation(member, conversationId)] = true;
    }
    await updateValues(patch);

    /*
      Une ligne par arrivant, apres coup : la porte est deja ouverte, et
      celui qui vient d'entrer doit pouvoir lire ce qui l'annonce.
    */
    for (const member of added) {
      await noterMouvementDeGroupe(conversationId, uid, member, 'ajout-groupe').catch(
        () => undefined,
      );
    }
  }

  const next: ConversationRecord = {
    ...record,
    members: { ...record.members, ...membersMap(added) },
  };
  const [conversation] = await buildConversations([{ id: conversationId, record: next }], uid);
  return conversation;
}

/**
 * Quitte un groupe.
 *
 * Les deux cotes du lien tombent d'un coup : la conversation ne doit ni me
 * compter parmi ses membres, ni rester dans ma liste.
 *
 * L'administrateur ne part pas les mains vides. Ses pouvoirs — retirer
 * quelqu'un, nommer un successeur — n'appartiennent qu'a lui ; s'il s'en
 * va sans les passer, le groupe reste sans personne pour l'ouvrir ou le
 * ranger. Il designe donc son repreneur en sortant, dans la meme
 * ecriture : entre les deux, le groupe n'est jamais sans chef.
 *
 * Dernier membre, la question ne se pose pas : il n'y a personne a qui
 * laisser la main.
 */
/**
 * Depose dans le groupe la trace d'un depart.
 *
 * Posee **avant** de partir : la regle des messages ne s'ouvre qu'aux
 * membres du fil, et l'on n'en est plus une fois sorti.
 *
 * Le pseudo voyage avec la ligne, contrairement a tout le reste de
 * l'application ou l'on garde l'identifiant pour que le nom reste frais.
 * Ici il ne le resterait pas : la liste des auteurs se batit sur les
 * membres du groupe, et celui qui part n'en est plus un — la ligne aurait
 * annonce le depart de personne.
 *
 * Un echec est avale : rater le mot ne doit pas empecher de sortir.
 */
async function noterMouvementDeGroupe(
  conversationId: string,
  uid: string,
  /*
    De qui l'on parle, quand ce n'est pas celui qui ecrit. L'admin retire
    quelqu'un : c'est lui qui pose la ligne, mais elle nomme l'autre.
  */
  sujet: string = uid,
  systeme: 'sortie-groupe' | 'retire-groupe' | 'ajout-groupe' = 'sortie-groupe',
): Promise<void> {
  const pseudo = (await readOptionalValue<string>(paths.userField(sujet, 'username'))) ?? '';
  await pushValue(paths.messages(conversationId), {
    senderId: uid,
    content: pseudo,
    createdAt: Date.now(),
    attachedEventId: null,
    imageUrl: null,
    systeme,
  } satisfies MessageRecord);
}

export async function leaveGroup(conversationId: string, successorId?: string): Promise<void> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);
  if (record.type !== 'group') {
    throw new SiroccoDataError(t.messages.erreurs.priveNeSeQuittePas, 'not-a-group');
  }

  const others = keysOf(record.members).filter((id) => id !== uid);
  /*
    Deux departs laissent le groupe sans chef : celui de l'administrateur,
    et celui de n'importe qui dans un groupe qui n'en a deja plus — les
    groupes d'avant cette regle ont pu perdre le leur. Les deux reclament
    donc un repreneur, et le second repare au passage.
  */
  const handsOver = others.length > 0 && (!record.adminId || record.adminId === uid);

  /*
    Le garde-fou plutot que la consigne : les deux sorties — la fiche du
    groupe, la liste des conversations — posent la question avant
    d'appeler. Si l'une d'elles l'oubliait, le groupe ne partirait pas
    sans chef pour autant.
  */
  if (handsOver && (!successorId || !others.includes(successorId))) {
    throw new SiroccoDataError(t.messages.erreurs.successeur, 'needs-successor');
  }

  await noterMouvementDeGroupe(conversationId, uid).catch(() => undefined);

  await updateValues({
    [paths.conversationMember(conversationId, uid)]: null,
    [paths.userConversation(uid, conversationId)]: null,
    ...(handsOver ? { [`${paths.conversation(conversationId)}/adminId`]: successorId } : {}),
  });
}

/** Le meme mot, pour qui quitte le groupe en quittant la sortie. */
export async function noterSortieDuGroupeDeSortie(conversationId: string): Promise<void> {
  await noterMouvementDeGroupe(conversationId, requireUid()).catch(() => undefined);
}

/**
 * Et celui de l'arrivee, pour qui rejoint le groupe d'une sortie.
 *
 * Deux chemins y menent — le raccourci de la fiche, et la validation par
 * l'organisateur — et aucun des deux ne disait rien : on voyait le
 * compteur de membres monter sans savoir qui venait d'entrer, alors qu'un
 * ajout a la main, lui, s'annonce depuis ce soir.
 *
 * Toujours apres l'entree : la regle des messages ne s'ouvre qu'aux
 * membres du fil.
 */
export async function noterArriveeDansLeGroupeDeSortie(
  conversationId: string,
  memberId: string,
): Promise<void> {
  await noterMouvementDeGroupe(conversationId, requireUid(), memberId, 'ajout-groupe').catch(
    () => undefined,
  );
}

/** Les personnes a qui je peux ecrire : mes amis. */
export async function listContacts(): Promise<PublicUser[]> {
  const uid = requireUid();
  const ids = await readKeys(paths.friends(uid));
  if (ids.length === 0) return [];

  const users = await loadUsers(ids);
  return ids
    .map((id) => users.get(id) ?? unknownUser(id))
    .sort((a, b) => a.username.localeCompare(b.username, 'fr'));
}

/**
 * Depose la trace d'un appel dans la discussion.
 *
 * Ecrite par celui qui a appele, et par lui seul : c'est le seul des deux
 * a savoir comment l'appel s'est termine. Le telephone de l'autre n'a
 * peut-etre jamais sonne — application fermee — et il ne verra donc ceci
 * qu'en rouvrant la discussion, ce qui est precisement le but.
 *
 * Un echec est avale : rater la trace d'un appel ne doit pas faire
 * remonter une erreur a un ecran qui vient de raccrocher.
 */
/** Ce qui voyage dans une discussion : un post, ou un evenement. */
export type ChoseAPartager = { postId: string } | { eventId: string };

/**
 * Partage un post ou un evenement dans la discussion directe avec
 * quelqu'un.
 *
 * On envoie l'identifiant, pas un lien : le destinataire est dans
 * Sirocco, lui donner une adresse a rouvrir dans un navigateur serait le
 * faire sortir pour le faire revenir. La carte se lit dans la
 * conversation, et le fil sait deja lire les deux.
 *
 * L'identifiant plutot qu'une copie du texte : la chose se lit telle
 * qu'elle est maintenant — un evenement deplace se lit deplace — et ce
 * qui est efface ne laisse pas de double derriere lui.
 *
 * La conversation s'ouvre si elle n'existait pas — partager a un ami a
 * qui l'on n'a jamais ecrit doit marcher du premier coup. Le reste passe
 * par `sendMessage` : un partage est un message comme un autre, et une
 * seconde ecriture finirait par diverger de la premiere.
 */
export async function partagerAvec(
  otherId: string,
  serverId: string,
  quoi: ChoseAPartager,
): Promise<void> {
  const conversation = await openDirect(otherId, serverId);
  await partagerDans(conversation.id, quoi);
}

/**
 * Partage dans une conversation qui existe deja — un groupe.
 *
 * Rien a ouvrir : on est deja dedans, ou l'on n'a rien a y faire. La
 * regle d'amitie ne s'applique qu'aux tete-a-tete, etre membre suffit
 * ici — et c'est ce que verifie `sendMessage`, comme pour n'importe quel
 * autre message.
 */
export async function partagerDans(
  conversationId: string,
  quoi: ChoseAPartager,
): Promise<void> {
  const evenement = 'eventId' in quoi ? quoi.eventId : null;
  const post = 'postId' in quoi ? quoi.postId : null;
  await sendMessage(conversationId, '', evenement, null, null, null, post);
}

export async function noteCall(otherId: string, serverId: string, note: CallNote): Promise<void> {
  const uid = requireUid();
  const conversation = await openDirect(otherId, serverId);
  await pushValue(paths.messages(conversation.id), {
    senderId: uid,
    content: '',
    createdAt: Date.now(),
    attachedEventId: null,
    imageUrl: null,
    callMissed: note.missed,
    callVideo: note.video,
    callDurationMs: note.durationMs,
  } satisfies MessageRecord);
}

/**
 * Depose dans la discussion la trace d'une amitie rompue.
 *
 * Ecrite par celui qui retire, avant qu'il ne retire : la regle des
 * messages ne demande que d'etre membre du fil, mais l'ordre importe
 * quand meme — l'ecriture doit partir pendant que la conversation est
 * encore ouverte de son cote.
 *
 * **Rien si le fil n'existe pas.** On ne cree pas une conversation pour
 * annoncer sa fin ; deux personnes qui ne se sont jamais ecrit n'ont pas
 * besoin d'un mot.
 *
 * Un echec est avale : rater cette ligne ne doit pas empecher de retirer
 * quelqu'un de ses amis.
 */
export async function noterAmitieRompue(otherUid: string): Promise<void> {
  const uid = requireUid();
  const conversationId = await readOptionalValue<string>(
    paths.directConversation(uid, otherUid),
  );
  if (!conversationId) return;
  if (!(await readOptionalValue(paths.conversation(conversationId)))) return;

  await pushValue(paths.messages(conversationId), {
    senderId: uid,
    content: '',
    createdAt: Date.now(),
    attachedEventId: null,
    imageUrl: null,
    systeme: 'amitie-rompue',
  } satisfies MessageRecord);

  /*
    Et la conversation se referme vraiment.

    Deux drapeaux la tenaient ouverte par-dessus l'amitie, et les deux
    survivaient a la rupture. `opened` dit qu'une demande a ete acceptee
    et vaut autorisation permanente ; `request` laisse le destinataire
    d'une demande ecrire librement. Ni l'un ni l'autre n'a de sens une
    fois l'amitie defaite : on continuait a s'ecrire sous un bandeau qui
    annonce le contraire.

    Ils partent donc avec elle. Ce qui a ete dit reste, et redevenir amis
    rouvre le fil — c'est exactement ce que le bandeau promet.
  */
  await updateValues({
    [`${paths.conversation(conversationId)}/opened`]: null,
    [`${paths.conversation(conversationId)}/request`]: null,
    [`${paths.conversation(conversationId)}/requestAt`]: null,
    [`${paths.conversation(conversationId)}/requestSent`]: null,
  });
}

/* --- « Il écrit… » ---------------------------------------------------- */

/**
 * Le seul signal qui coute presque rien et qui change tout.
 *
 * Un booleen par personne et par fil, ecrit au plus une fois toutes les
 * trois secondes pendant qu'on tape, efface a l'envoi. `onDisconnect`
 * s'en charge si l'application meurt en cours de frappe — sans lui, un
 * « il écrit… » resterait affiche pour toujours.
 *
 * L'ecoute ne vit que dans le fil ouvert. C'est ce qui la garde bornee :
 * elle ne pese rien de plus qu'une conversation a la fois.
 */
const FRAPPE_MS = 3000;

/* Par fil, et non globalement : passer d'une conversation a l'autre en
   moins de trois secondes ne doit pas avaler le signal. */
const derniereFrappe = new Map<string, number>();

export function signalerFrappe(conversationId: string): void {
  const uid = requireUid();
  const maintenant = Date.now();
  if (maintenant - (derniereFrappe.get(conversationId) ?? 0) < FRAPPE_MS) return;
  derniereFrappe.set(conversationId, maintenant);

  const noeud = ref(db(), paths.typing(conversationId, uid));
  void set(noeud, maintenant).catch(() => undefined);
  // L'application peut mourir la main sur le clavier.
  void onDisconnect(noeud).remove().catch(() => undefined);
}

/** Plus rien a dire : on efface tout de suite. */
export function arreterFrappe(conversationId: string): void {
  const uid = requireUid();
  derniereFrappe.delete(conversationId);
  void remove(ref(db(), paths.typing(conversationId, uid))).catch(() => undefined);
}

/**
 * Qui ecrit en ce moment dans ce fil, moi excepte.
 *
 * On se fie a la date posee plutot qu'a la seule presence du noeud : un
 * telephone qui perd le reseau sans se deconnecter proprement laisse une
 * marque derriere lui, et « il écrit… » ne doit pas survivre a la
 * conversation. Passe deux fois le delai de frappe, la marque ne compte
 * plus.
 */
export function watchFrappe(
  conversationId: string,
  onChange: (uids: string[]) => void,
): () => void {
  const uid = requireUid();
  return onValue(ref(db(), paths.typingOf(conversationId)), (snap) => {
    const marques = (snap.val() as Record<string, number> | null) ?? {};
    const limite = Date.now() - 2 * FRAPPE_MS;
    onChange(
      Object.entries(marques)
        .filter(([qui, quand]) => qui !== uid && quand > limite)
        .map(([qui]) => qui),
    );
  });
}

/**
 * Qui ecrit, dans laquelle de mes conversations.
 *
 * Une ecoute par fil, mais sur la plus petite branche de la base : un
 * horodatage par personne. Rien du message ne transite — c'est ce qui
 * permet de tenir la promesse « pas d'ecoute couteuse sur la liste ».
 * Elle ne vit que tant que l'ecran des conversations est ouvert.
 *
 * Les marques s'eteignent d'elles-memes, et **leur extinction ne produit
 * aucun evenement** : personne n'ecrit en base « il a cesse d'ecrire ».
 * D'ou le reveil programme sur la plus proche echeance, qui recalcule
 * sans rien relire.
 */
export function watchFrappeDesFils(
  conversationIds: string[],
  onChange: (fils: Set<string>) => void,
): () => void {
  const uid = requireUid();
  const marques = new Map<string, number>();
  let reveil: ReturnType<typeof setTimeout> | null = null;
  let vivant = true;

  function recalculer() {
    if (!vivant) return;
    if (reveil) clearTimeout(reveil);

    const limite = Date.now() - 2 * FRAPPE_MS;
    const actifs = new Set<string>();
    let prochaine = Infinity;

    for (const [conversationId, quand] of marques) {
      if (quand > limite) {
        actifs.add(conversationId);
        prochaine = Math.min(prochaine, quand + 2 * FRAPPE_MS);
      }
    }
    onChange(actifs);

    if (prochaine < Infinity) {
      reveil = setTimeout(recalculer, Math.max(500, prochaine - Date.now()));
    }
  }

  const arrets = conversationIds.map((conversationId) =>
    onValue(ref(db(), paths.typingOf(conversationId)), (snap) => {
      const parPersonne = (snap.val() as Record<string, number> | null) ?? {};
      // La derniere marque posee par quelqu'un d'autre que moi.
      const autres = Object.entries(parPersonne)
        .filter(([qui]) => qui !== uid)
        .map(([, quand]) => quand);

      if (autres.length > 0) marques.set(conversationId, Math.max(...autres));
      else marques.delete(conversationId);
      recalculer();
    }),
  );

  return () => {
    vivant = false;
    if (reveil) clearTimeout(reveil);
    for (const arret of arrets) arret();
  };
}

/**
 * De quoi annoncer un message sans l'ouvrir.
 *
 * Lit le dernier message d'un fil et qui l'a ecrit, **sans rien marquer
 * comme lu** : c'est toute la difference avec `listThread`, qui pose le
 * repere de lecture au passage. Une banniere qui eteindrait le compteur
 * de non-lus annoncerait un message et le ferait disparaitre du meme
 * geste.
 */
export async function apercuDuFil(conversationId: string): Promise<{
  titre: string;
  auteur: PublicUser;
  texte: string;
  groupe: boolean;
} | null> {
  const uid = requireUid();
  const record = await requireMembership(conversationId, uid);

  const derniers = await readList<MessageRecord>(paths.messages(conversationId), {
    orderBy: 'createdAt',
    limit: 1,
  });
  const dernier = derniers.at(-1);
  // Rien a annoncer si c'est moi qui viens d'ecrire.
  if (!dernier || dernier.senderId === uid || dernier.deleted) return null;

  const gens = await loadUsers([dernier.senderId]);
  const auteur = gens.get(dernier.senderId) ?? unknownUser(dernier.senderId);
  const groupe = record.type === 'group';

  return {
    titre: groupe ? (record.title ?? t.messages.apercu.groupe) : auteur.username,
    auteur,
    texte: apercuCourt(dernier),
    groupe,
  };
}

/** Ce qu'on montre du message : son texte, ou ce qu'il est. */
function apercuCourt(message: MessageRecord): string {
  if (message.imageUrl) return t.messages.apercu.photo;
  if (message.audioUrl) return t.messages.apercu.vocal;
  if (message.videoUrl) return t.messages.apercu.video;
  if (message.attachedEventId) return t.messages.apercu.sortie;
  if (message.attachedPostId) return t.messages.apercu.post;
  const texte = (message.content ?? '').trim();
  return texte.length > 90 ? `${texte.slice(0, 89)}…` : texte || t.messages.apercu.nouveau;
}

/* Les emojis poses sur les messages d'un fil, par message puis par membre. */
export type Reactions = Record<string, Record<string, string>>;

/**
 * Pose, remplace ou retire son emoji sur un message.
 *
 * Chacun n'en a qu'un par message : reposer le meme le retire, en poser
 * un autre remplace. C'est la regle de toutes les messageries, et elle
 * evite qu'un membre seul couvre un message de pastilles.
 */
export async function reagir(
  conversationId: string,
  messageId: string,
  emoji: string | null,
): Promise<void> {
  const uid = requireUid();
  const chemin = paths.reaction(conversationId, messageId, uid);
  if (emoji === null) await remove(ref(db(), chemin));
  else await set(ref(db(), chemin), emoji);
}

/**
 * Les reactions du fil, lues une fois.
 *
 * Elles partent avec le fil plutot qu'apres lui : l'ecoute ne repond
 * qu'une fraction de seconde plus tard, et pendant ce temps les
 * pastilles manquaient sous les bulles qui en portent — elles
 * apparaissaient ensuite, en grandissant les messages. Un fil qui
 * s'ouvre doit etre fini quand il s'affiche.
 *
 * Lecture facultative : leur absence, ou un refus de la base, ne doit
 * pas emporter la conversation entiere.
 */
export async function lireReactions(conversationId: string): Promise<Reactions> {
  return (await readOptionalValue<Reactions>(paths.reactionsOf(conversationId))) ?? {};
}

/**
 * Ecoute les reactions du fil entier.
 *
 * Une seule ecoute pour toute la conversation plutot qu'une par message :
 * ce sont quelques octets par reaction, et le fil se defile — poser une
 * ecoute par bulle affichee les multiplierait sans fin.
 */
export function watchReactions(
  conversationId: string,
  onChange: (reactions: Reactions) => void,
): () => void {
  return onValue(ref(db(), paths.reactionsOf(conversationId)), (snap) => {
    onChange((snap.val() as Reactions | null) ?? {});
  });
}
